import { useEffect, useState } from 'react';
import CourseDatePicker from './CourseDatePicker';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

function headers(extra = {}) {
  return { apikey: SUPABASE_KEY, Authorization: 'Bearer ' + SUPABASE_KEY, ...extra };
}

async function readJson(response, label) {
  const body = await response.text();
  if (!response.ok) throw new Error(`${label} failed (${response.status}): ${body || response.statusText}`);
  return body ? JSON.parse(body) : null;
}

function dateValue(value) {
  return value ? String(value).slice(0, 10) : '';
}

function newInstance(order) {
  return {
    id: `new-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    instance_order: order,
    open_date: '',
    close_date: '',
    schedule_open_offset_days: '',
    schedule_close_offset_days: '',
    section_ids: [],
    isNew: true,
    locked: false
  };
}

async function loadConfiguration(courseAssignmentId) {
  const id = encodeURIComponent(courseAssignmentId);
  const [sectionsResponse, instancesResponse] = await Promise.all([
    fetch(`${SUPABASE_URL}/rest/v1/course_structured_sections?course_assignment_id=eq.${id}&select=id,title,description,section_order&order=section_order.asc`, { headers: headers() }),
    fetch(`${SUPABASE_URL}/rest/v1/course_structured_instances?course_assignment_id=eq.${id}&select=*&order=instance_order.asc`, { headers: headers() })
  ]);

  const sections = await readJson(sectionsResponse, 'Loading form sections');
  const instances = await readJson(instancesResponse, 'Loading form instances');
  const instanceIds = (instances || []).map(row => row.id);
  if (!instanceIds.length) {
    return {
      sections: sections || [],
      instances: [],
      groupDeadlines: []
    };
  }

  const inFilter = `(${instanceIds.map(encodeURIComponent).join(',')})`;
  const [
    linksResponse,
    submissionsResponse,
    groupDeadlinesResponse
  ] = await Promise.all([
    fetch(`${SUPABASE_URL}/rest/v1/course_structured_instance_sections?course_structured_instance_id=in.${inFilter}&select=*`, { headers: headers() }),
    fetch(`${SUPABASE_URL}/rest/v1/assignment_submissions?structured_form_instance_id=in.${inFilter}&submitted=eq.true&select=structured_form_instance_id`, { headers: headers() }),
    fetch(`${SUPABASE_URL}/rest/v1/course_structured_instance_group_deadlines?course_structured_instance_id=in.${inFilter}&select=*`, { headers: headers() })
  ]);

  const links = await readJson(
    linksResponse,
    'Loading instance sections'
  );

  const submissions = await readJson(
    submissionsResponse,
    'Checking instance submissions'
  );

  const groupDeadlines = await readJson(
    groupDeadlinesResponse,
    'Loading Structured Form group deadlines'
  );
  const lockedIds = new Set((submissions || []).map(row => row.structured_form_instance_id));

  return {
    sections: sections || [],
    instances: (instances || []).map(instance => ({
      ...instance,
      open_date: dateValue(instance.open_date),
      close_date: dateValue(instance.close_date),
      schedule_open_offset_days:
        instance.schedule_open_offset_days === null ||
        instance.schedule_open_offset_days === undefined
          ? ''
          : String(
              instance.schedule_open_offset_days
            ),
      schedule_close_offset_days:
        instance.schedule_close_offset_days === null ||
        instance.schedule_close_offset_days === undefined
          ? ''
          : String(
              instance.schedule_close_offset_days
            ),
      section_ids: (links || []).filter(link => link.course_structured_instance_id === instance.id).map(link => link.course_structured_section_id),
      isNew: false,
      locked: lockedIds.has(instance.id)
    })),
    groupDeadlines:
      Array.isArray(groupDeadlines)
        ? groupDeadlines
        : []
  };
}

function structuredScheduleInFilter(values) {
  return [
    ...new Set(
      (values || [])
        .filter(Boolean)
        .map(String)
    )
  ].join(',');
}

async function loadStructuredScheduleItems(
  instanceIds
) {
  const ids =
    structuredScheduleInFilter(
      instanceIds
    );

  if (!ids) return [];

  const itemResponse = await fetch(
    `${SUPABASE_URL}/rest/v1/course_structured_instance_schedule_items?course_structured_instance_id=in.(${ids})&select=*&order=display_order.asc,created_at.asc`,
    { headers: headers() }
  );

  const items = await readJson(
    itemResponse,
    'Loading Structured Form Schedule items'
  );

  const rows =
    Array.isArray(items) ? items : [];

  if (!rows.length) return [];

  const itemIds =
    structuredScheduleInFilter(
      rows.map(row => row.id)
    );

  const selectionResponse = await fetch(
    `${SUPABASE_URL}/rest/v1/course_structured_instance_schedule_selections?course_structured_instance_schedule_item_id=in.(${itemIds})&select=id,course_structured_instance_schedule_item_id`,
    { headers: headers() }
  );

  const selections = await readJson(
    selectionResponse,
    'Checking Structured Form Schedule choices'
  );

  const usedIds = new Set(
    (Array.isArray(selections)
      ? selections
      : []
    ).map(row =>
      String(
        row.course_structured_instance_schedule_item_id
      )
    )
  );

  return rows.map(row => ({
    ...row,
    hasSelections:
      usedIds.has(String(row.id)),
    isNew: false
  }));
}

async function saveStructuredScheduleTypeSettings(
  assignment,
  data
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_type_settings?on_conflict=course_id,assignment_type_id`,
    {
      method: 'POST',
      headers: headers({
        'Content-Type': 'application/json',
        Prefer: 'resolution=merge-duplicates'
      }),
      body: JSON.stringify({
        course_id: assignment.course_id,
        assignment_type_id:
          assignment.assignment_type_id,
        ...data
      })
    }
  );

  if (!response.ok) {
    const body = await response.text();

    throw new Error(
      `Saving Structured Form Schedule setting failed (${response.status}): ${body || response.statusText}`
    );
  }
}

async function createStructuredScheduleItem(
  data
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_structured_instance_schedule_items`,
    {
      method: 'POST',
      headers: headers({
        'Content-Type': 'application/json',
        Prefer: 'return=representation'
      }),
      body: JSON.stringify(data)
    }
  );

  const rows = await readJson(
    response,
    'Adding Structured Form Schedule item'
  );

  return rows?.[0] || null;
}

async function updateStructuredScheduleItem(
  id,
  data
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_structured_instance_schedule_items?id=eq.${encodeURIComponent(id)}`,
    {
      method: 'PATCH',
      headers: headers({
        'Content-Type': 'application/json',
        Prefer: 'return=representation'
      }),
      body: JSON.stringify(data)
    }
  );

  const rows = await readJson(
    response,
    'Updating Structured Form Schedule item'
  );

  return rows?.[0] || null;
}

async function deleteStructuredScheduleItem(
  id
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_structured_instance_schedule_items?id=eq.${encodeURIComponent(id)}`,
    {
      method: 'DELETE',
      headers: headers()
    }
  );

  await readJson(
    response,
    'Removing Structured Form Schedule item'
  );
}

function scheduleSourceOptions(
  readings,
  events
) {
  const readingOptions =
    (Array.isArray(readings)
      ? readings
      : []
    ).map(row => ({
      value: `reading:${row.id}`,
      item_type: 'reading',
      source_id: row.id,
      label: [
        row.course_weeks?.week_number
          ? `Week ${row.course_weeks.week_number}`
          : null,
        row.course_readings?.title ||
          'Reading',
        row.course_readings?.author
          ? `by ${row.course_readings.author}`
          : null
      ].filter(Boolean).join(' · ')
    }));

  const eventOptions =
    (Array.isArray(events)
      ? events
      : []
    ).map(row => ({
      value: `event:${row.id}`,
      item_type: 'event',
      source_id: row.id,
      label: [
        row.course_weeks?.week_number
          ? `Week ${row.course_weeks.week_number}`
          : null,
        row.title || 'Event',
        row.event_date || null
      ].filter(Boolean).join(' · ')
    }));

  return [
    ...readingOptions,
    ...eventOptions
  ];
}

function scheduleItemValue(item) {
  if (!item) return '';

  if (
    item.item_type === 'reading' &&
    item.week_reading_assignment_id
  ) {
    return (
      'reading:' +
      item.week_reading_assignment_id
    );
  }

  if (
    item.item_type === 'event' &&
    item.course_event_id
  ) {
    return (
      'event:' +
      item.course_event_id
    );
  }

  return '';
}

function structuredCalendarAddDays(
  value,
  offset
) {
  if (
    !value ||
    offset === '' ||
    offset === null ||
    offset === undefined ||
    !Number.isInteger(
      Number(offset)
    )
  ) {
    return null;
  }

  const parts =
    String(value)
      .slice(0, 10)
      .split('-')
      .map(Number);

  if (
    parts.length !== 3 ||
    !parts.every(
      Number.isFinite
    )
  ) {
    return null;
  }

  const date =
    new Date(
      Date.UTC(
        parts[0],
        parts[1] - 1,
        parts[2]
      )
    );

  date.setUTCDate(
    date.getUTCDate() +
    Number(offset)
  );

  return date
    .toISOString()
    .slice(0, 10);
}


function structuredCalendarScheduleSource(
  item,
  readings,
  events
) {
  if (
    item?.item_type ===
    'reading'
  ) {
    const reading =
      (readings || [])
        .find(
          candidate =>
            String(
              candidate.id
            ) ===
            String(
              item
                .week_reading_assignment_id ||
              ''
            )
        );

    return {
      date:
        reading
          ?.course_weeks
          ?.class_date ||
        null,

      label:
        reading
          ?.course_readings
          ?.title ||
        'Reading'
    };
  }

  const event =
    (events || [])
      .find(
        candidate =>
          String(
            candidate.id
          ) ===
          String(
            item
              ?.course_event_id ||
            ''
          )
      );

  return {
    date:
      event?.event_date ||
      event
        ?.course_weeks
        ?.class_date ||
      null,

    label:
      event?.title ||
      'Event'
  };
}


function schedulePayloadFromOption(
  optionValue
) {
  const [itemType, sourceId] =
    String(optionValue || '').split(':');

  if (
    !sourceId ||
    !['reading', 'event'].includes(
      itemType
    )
  ) {
    return null;
  }

  return {
    item_type: itemType,
    week_reading_assignment_id:
      itemType === 'reading'
        ? sourceId
        : null,
    course_event_id:
      itemType === 'event'
        ? sourceId
        : null
  };
}

async function patchRow(table, id, data, label) {
  const response = await fetch(`${SUPABASE_URL}/rest/v1/${table}?id=eq.${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: headers({ 'Content-Type': 'application/json', Prefer: 'return=representation' }),
    body: JSON.stringify(data)
  });
  return readJson(response, label);
}

async function deleteInstance(id) {
  const response = await fetch(`${SUPABASE_URL}/rest/v1/course_structured_instances?id=eq.${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: headers()
  });
  await readJson(response, 'Deleting form instance');
}

async function saveInstance(instance, courseAssignmentId) {
  const payload = {
    course_assignment_id: courseAssignmentId,
    instance_order: instance.instance_order,
    open_date: instance.open_date || null,
    close_date: instance.close_date || null,
    schedule_open_offset_days:
      instance.schedule_open_offset_days === ''
        ? null
        : Number(
            instance.schedule_open_offset_days
          ),
    schedule_close_offset_days:
      instance.schedule_close_offset_days === ''
        ? null
        : Number(
            instance.schedule_close_offset_days
          ),
    updated_at: new Date().toISOString()
  };

  if (instance.isNew) {
    const response = await fetch(`${SUPABASE_URL}/rest/v1/course_structured_instances`, {
      method: 'POST',
      headers: headers({ 'Content-Type': 'application/json', Prefer: 'return=representation' }),
      body: JSON.stringify(payload)
    });
    const rows = await readJson(response, 'Creating form instance');
    return rows?.[0];
  }

  const rows = await patchRow('course_structured_instances', instance.id, payload, 'Saving form instance');
  return rows?.[0] || { ...instance, ...payload };
}

async function replaceInstanceSections(instanceId, sectionIds) {
  const deleteResponse = await fetch(`${SUPABASE_URL}/rest/v1/course_structured_instance_sections?course_structured_instance_id=eq.${encodeURIComponent(instanceId)}`, {
    method: 'DELETE',
    headers: headers()
  });
  await readJson(deleteResponse, 'Updating instance sections');
  if (!sectionIds.length) return;

  const response = await fetch(`${SUPABASE_URL}/rest/v1/course_structured_instance_sections`, {
    method: 'POST',
    headers: headers({ 'Content-Type': 'application/json', Prefer: 'return=minimal' }),
    body: JSON.stringify(sectionIds.map(sectionId => ({
      course_structured_instance_id: instanceId,
      course_structured_section_id: sectionId
    })))
  });
  await readJson(response, 'Saving instance sections');
}

function buildGroupDeadlineDraft(rows) {
  const result = {};

  (Array.isArray(rows) ? rows : []).forEach(row => {
    const instanceId =
      row.course_structured_instance_id;

    const groupNumber =
      Number(row.group_number);

    if (!instanceId || !groupNumber) return;

    if (!result[instanceId]) {
      result[instanceId] = {};
    }

    result[instanceId][groupNumber] = {
      open_date: dateValue(row.open_date),
      close_date: dateValue(row.close_date)
    };
  });

  return result;
}

async function saveGroupDeadline(
  instanceId,
  groupNumber,
  deadline
) {
  const openDate =
    dateValue(deadline?.open_date);

  const closeDate =
    dateValue(deadline?.close_date);

  if (!openDate && !closeDate) {
    const response = await fetch(
      `${SUPABASE_URL}/rest/v1/course_structured_instance_group_deadlines?course_structured_instance_id=eq.${encodeURIComponent(instanceId)}&group_number=eq.${groupNumber}`,
      {
        method: 'DELETE',
        headers: headers()
      }
    );

    await readJson(
      response,
      'Removing Structured Form group deadline'
    );

    return;
  }

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_structured_instance_group_deadlines?on_conflict=course_structured_instance_id,group_number`,
    {
      method: 'POST',
      headers: headers({
        'Content-Type': 'application/json',
        Prefer:
          'resolution=merge-duplicates,return=representation'
      }),
      body: JSON.stringify({
        course_structured_instance_id:
          instanceId,
        group_number: groupNumber,
        open_date: openDate || null,
        close_date: closeDate || null,
        updated_at: new Date().toISOString()
      })
    }
  );

  await readJson(
    response,
    'Saving Structured Form group deadline'
  );
}

function validateGroupDeadlines(
  instances,
  groupDeadlines,
  groupCount
) {
  const ordered = [...instances].sort(
    (a, b) =>
      Number(a.instance_order) -
      Number(b.instance_order)
  );

  for (
    let groupNumber = 1;
    groupNumber <= Number(groupCount || 0);
    groupNumber += 1
  ) {
    let previousClose = null;
    let previousOrder = null;

    for (const instance of ordered) {
      const deadline =
        groupDeadlines?.[instance.id]
          ?.[groupNumber] || {};

      const openDate =
        dateValue(deadline.open_date) ||
        dateValue(instance.open_date);

      const closeDate =
        dateValue(deadline.close_date) ||
        dateValue(instance.close_date);

      if (
        openDate &&
        closeDate &&
        openDate > closeDate
      ) {
        return (
          'Group ' + groupNumber + ', Instance ' +
          instance.instance_order +
          ' cannot close before it opens.'
        );
      }

      if (
        previousClose &&
        openDate &&
        previousClose >= openDate
      ) {
        return (
          'Group ' + groupNumber + ', Instance ' +
          instance.instance_order +
          ' must open after Instance ' +
          previousOrder + ' closes.'
        );
      }

      previousClose = closeDate || previousClose;
      previousOrder = instance.instance_order;
    }
  }

  return '';
}

function validateStructuredSchedule(
  instances,
  scheduleItems,
  choiceEnabled
) {
  for (const instance of instances) {
    const label =
      `Instance ${instance.instance_order}`;

    if (
      instance.schedule_open_offset_days === '' ||
      !Number.isInteger(
        Number(
          instance.schedule_open_offset_days
        )
      )
    ) {
      return (
        `${label} needs an integer Open offset.`
      );
    }

    if (
      instance.schedule_close_offset_days === '' ||
      !Number.isInteger(
        Number(
          instance.schedule_close_offset_days
        )
      )
    ) {
      return (
        `${label} needs an integer Close offset.`
      );
    }

    const mode =
      choiceEnabled
        ? 'choice'
        : 'single';

    const activeItems =
      scheduleItems.filter(
        item =>
          String(
            item.course_structured_instance_id
          ) === String(instance.id) &&
          item.configuration_mode ===
            mode &&
          item.deleted !== true
      );

    if (
      choiceEnabled &&
      activeItems.length < 1
    ) {
      return (
        `${label} needs at least one Schedule choice.`
      );
    }

    if (
      !choiceEnabled &&
      activeItems.length !== 1
    ) {
      return (
        `${label} needs exactly one Schedule anchor.`
      );
    }

    if (
      !instance.section_ids.length
    ) {
      return (
        `${label} must include at least one section.`
      );
    }
  }

  return '';
}

function validate(overall, instances) {
  if (!overall.open_date || !overall.due_date) return 'Set the overall Open and Final due dates.';
  if (overall.open_date > overall.due_date) return 'The overall Open date cannot be after the Final due date.';
  if (!instances.length) return 'Add at least one instance.';

  const ordered = [...instances].sort((a, b) => Number(a.instance_order) - Number(b.instance_order));
  for (let index = 0; index < ordered.length; index += 1) {
    const instance = ordered[index];
    const label = `Instance ${instance.instance_order}`;
    if (!instance.open_date || !instance.close_date) return `${label} needs Open and Close dates.`;
    if (instance.open_date > instance.close_date) return `${label} cannot close before it opens.`;
    if (instance.open_date < overall.open_date || instance.close_date > overall.due_date) {
      return `${label} must fall within the overall assignment dates.`;
    }
    if (!instance.section_ids.length) return `${label} must include at least one section.`;
    if (index > 0 && ordered[index - 1].close_date >= instance.open_date) {
      return `${label} must open after Instance ${ordered[index - 1].instance_order} closes.`;
    }
  }
  return '';
}

export default function StructuredFormScheduleEditor({
  assignment,
  label,
  scheduleEnabled = false,
  scheduleChoiceEnabled = false,
  scheduleChoiceOpenDate = '',
  scheduleChoiceCloseDate = '',
  course = null,
  calendarWeeks = [],
  calendarAssignments = [],
  calendarTypeSettings = {},
  calendarScheduleItems = [],
  calendarHolidays = [],
  scheduleReadings = [],
  scheduleEvents = [],
  onScheduleTypeSettingsChanged,
  groupDeadlinesEnabled = false,
  groupNameFor = groupNumber => `Group ${groupNumber}`,
  onAssignmentChanged
}) {
  const [overall, setOverall] = useState({
    open_date: dateValue(assignment.open_date),
    due_date: dateValue(assignment.due_date)
  });
  const [sections, setSections] = useState([]);
  const [instances, setInstances] = useState([]);
  const [
    structuredScheduleItems,
    setStructuredScheduleItems
  ] = useState([]);
  const [
    deletedScheduleItemIds,
    setDeletedScheduleItemIds
  ] = useState([]);
  const [
    localChoiceEnabled,
    setLocalChoiceEnabled
  ] = useState(!!scheduleChoiceEnabled);
  const [
    choiceOpenDate,
    setChoiceOpenDate
  ] = useState(scheduleChoiceOpenDate || '');
  const [
    choiceCloseDate,
    setChoiceCloseDate
  ] = useState(scheduleChoiceCloseDate || '');
  const [
    scheduleSettingSaving,
    setScheduleSettingSaving
  ] = useState(false);
  const [groupDeadlines, setGroupDeadlines] =
    useState({});
  const [deletedIds, setDeletedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    setLocalChoiceEnabled(
      !!scheduleChoiceEnabled
    );
  }, [scheduleChoiceEnabled]);

  useEffect(() => {
    setChoiceOpenDate(
      scheduleChoiceOpenDate || ''
    );
    setChoiceCloseDate(
      scheduleChoiceCloseDate || ''
    );
  }, [
    scheduleChoiceOpenDate,
    scheduleChoiceCloseDate
  ]);

  useEffect(() => {
    setOverall({ open_date: dateValue(assignment.open_date), due_date: dateValue(assignment.due_date) });
  }, [assignment.id, assignment.open_date, assignment.due_date]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    loadConfiguration(assignment.id)
      .then(async result => {
        const scheduleRows =
          await loadStructuredScheduleItems(
            result.instances.map(
              instance => instance.id
            )
          );

        if (cancelled) return;
        setSections(result.sections);
        setInstances(result.instances);
        setStructuredScheduleItems(
          scheduleRows
        );
        setDeletedScheduleItemIds([]);
        setGroupDeadlines(
          buildGroupDeadlineDraft(
            result.groupDeadlines
          )
        );
        setDeletedIds([]);
      })
      .catch(err => {
        if (!cancelled) setError(err?.message || 'Could not load this Structured Form.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [assignment.id]);

  function updateInstance(id, patch) {
    setInstances(previous => previous.map(instance =>
      instance.id === id && !instance.locked ? { ...instance, ...patch } : instance
    ));
    setSuccess('');
  }

  const availableScheduleSources =
    scheduleSourceOptions(
      scheduleReadings,
      scheduleEvents
    );

  function itemsForInstance(
    instanceId,
    mode = null
  ) {
    return structuredScheduleItems
      .filter(
        item =>
          String(
            item.course_structured_instance_id
          ) === String(instanceId) &&
          item.deleted !== true &&
          (
            !mode ||
            item.configuration_mode === mode
          )
      )
      .sort(
        (a, b) =>
          Number(a.display_order || 0) -
          Number(b.display_order || 0)
      );
  }

  function setSingleAnchor(
    instanceId,
    optionValue
  ) {
    const payload =
      schedulePayloadFromOption(
        optionValue
      );

    const existing =
      itemsForInstance(
        instanceId,
        'single'
      )[0] || null;

    if (!payload) {
      if (existing?.id && !existing.isNew) {
        setDeletedScheduleItemIds(
          previous => [
            ...new Set([
              ...previous,
              existing.id
            ])
          ]
        );
      }

      setStructuredScheduleItems(
        previous =>
          previous.filter(
            item =>
              !(
                String(
                  item.course_structured_instance_id
                ) ===
                  String(instanceId) &&
                item.configuration_mode ===
                  'single'
              )
          )
      );

      return;
    }

    const next = {
      ...(existing || {}),
      id:
        existing?.id ||
        `new-schedule-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      course_structured_instance_id:
        instanceId,
      configuration_mode: 'single',
      display_order: 1,
      capacity: null,
      ...payload,
      isNew: !existing?.id ||
        String(existing.id).startsWith(
          'new-schedule-'
        )
    };

    setStructuredScheduleItems(
      previous => [
        ...previous.filter(
          item =>
            !(
              String(
                item.course_structured_instance_id
              ) ===
                String(instanceId) &&
              item.configuration_mode ===
                'single'
            )
        ),
        next
      ]
    );
  }

  function addChoiceOption(
    instanceId,
    optionValue
  ) {
    const payload =
      schedulePayloadFromOption(
        optionValue
      );

    if (!payload) return;

    const existingValues =
      new Set(
        itemsForInstance(
          instanceId,
          'choice'
        ).map(scheduleItemValue)
      );

    if (
      existingValues.has(optionValue)
    ) {
      return;
    }

    const current =
      itemsForInstance(
        instanceId,
        'choice'
      );

    setStructuredScheduleItems(
      previous => [
        ...previous,
        {
          id:
            `new-schedule-${Date.now()}-${Math.random().toString(36).slice(2)}`,
          course_structured_instance_id:
            instanceId,
          configuration_mode:
            'choice',
          display_order:
            current.length + 1,
          capacity: null,
          ...payload,
          isNew: true,
          hasSelections: false
        }
      ]
    );
  }

  function updateScheduleItemLocal(
    itemId,
    patch
  ) {
    setStructuredScheduleItems(
      previous =>
        previous.map(item =>
          String(item.id) ===
          String(itemId)
            ? {
                ...item,
                ...patch
              }
            : item
        )
    );
  }

  function removeScheduleItemLocal(
    item
  ) {
    if (item?.hasSelections) {
      setError(
        'That Schedule option already has student/group choices and cannot be removed.'
      );
      return;
    }

    if (
      item?.id &&
      !item.isNew
    ) {
      setDeletedScheduleItemIds(
        previous => [
          ...new Set([
            ...previous,
            item.id
          ])
        ]
      );
    }

    setStructuredScheduleItems(
      previous =>
        previous.filter(
          row =>
            String(row.id) !==
            String(item.id)
        )
    );
  }

  async function handleChoiceModeChange(
    enabled
  ) {
    setScheduleSettingSaving(true);
    setError('');
    setSuccess('');

    try {
      await saveStructuredScheduleTypeSettings(
        assignment,
        {
          schedule_choice_enabled:
            !!enabled
        }
      );

      setLocalChoiceEnabled(
        !!enabled
      );

      onScheduleTypeSettingsChanged?.({
        schedule_choice_enabled:
          !!enabled
      });

      setSuccess(
        `Choice ${enabled ? 'enabled' : 'disabled'}.`
      );
    } catch (err) {
      setError(
        err?.message ||
        'Could not save the Choice setting.'
      );
    } finally {
      setScheduleSettingSaving(false);
    }
  }

  async function saveChoiceWindow() {
    if (
      choiceOpenDate &&
      choiceCloseDate &&
      choiceOpenDate > choiceCloseDate
    ) {
      setError(
        'Choice cannot close before it opens.'
      );
      return;
    }

    setScheduleSettingSaving(true);
    setError('');
    setSuccess('');

    try {
      const patch = {
        schedule_choice_open_date:
          choiceOpenDate || null,
        schedule_choice_close_date:
          choiceCloseDate || null
      };

      await saveStructuredScheduleTypeSettings(
        assignment,
        patch
      );

      onScheduleTypeSettingsChanged?.(
        patch
      );

      setSuccess(
        'Choice window saved.'
      );
    } catch (err) {
      setError(
        err?.message ||
        'Could not save the Choice window.'
      );
    } finally {
      setScheduleSettingSaving(false);
    }
  }

  function updateGroupDeadline(
    instanceId,
    groupNumber,
    patch
  ) {
    setGroupDeadlines(previous => ({
      ...previous,
      [instanceId]: {
        ...(previous[instanceId] || {}),
        [groupNumber]: {
          ...(previous[instanceId]?.[groupNumber] || {}),
          ...patch
        }
      }
    }));

    setSuccess('');
  }

  function toggleSection(instance, sectionId) {
    const selected = new Set(instance.section_ids);
    if (selected.has(sectionId)) selected.delete(sectionId);
    else selected.add(sectionId);
    updateInstance(instance.id, { section_ids: Array.from(selected) });
  }

  function removeInstance(instance) {
    if (instance.locked) return;
    setInstances(previous => previous.filter(row => row.id !== instance.id));
    if (!instance.isNew) setDeletedIds(previous => [...previous, instance.id]);
    setSuccess('');
  }

  async function saveScheduleForInstance(
    sourceInstanceId,
    savedInstanceId
  ) {
    const items =
      structuredScheduleItems.filter(
        item =>
          String(
            item.course_structured_instance_id
          ) ===
            String(sourceInstanceId) &&
          item.deleted !== true
      );

    for (const item of items) {
      const payload = {
        course_structured_instance_id:
          savedInstanceId,
        item_type: item.item_type,
        week_reading_assignment_id:
          item.week_reading_assignment_id ||
          null,
        course_event_id:
          item.course_event_id || null,
        display_order:
          Number(item.display_order) || 1,
        capacity:
          item.configuration_mode ===
            'choice' &&
          item.capacity !== '' &&
          item.capacity !== null &&
          item.capacity !== undefined
            ? Number(item.capacity)
            : null,
        configuration_mode:
          item.configuration_mode,
        updated_at:
          new Date().toISOString()
      };

      if (
        item.isNew ||
        String(item.id).startsWith(
          'new-schedule-'
        )
      ) {
        await createStructuredScheduleItem(
          payload
        );
      } else {
        await updateStructuredScheduleItem(
          item.id,
          payload
        );
      }
    }
  }

  async function handleSave() {
    const validation =
      scheduleEnabled
        ? validateStructuredSchedule(
            instances,
            structuredScheduleItems,
            localChoiceEnabled
          )
        : validate(
            overall,
            instances
          );

    const groupValidation =
      !scheduleEnabled &&
      groupDeadlinesEnabled &&
      assignment.groups_enabled
        ? validateGroupDeadlines(
            instances,
            groupDeadlines,
            assignment.group_count
          )
        : '';

    if (validation || groupValidation) {
      setError(
        validation || groupValidation
      );
      setSuccess('');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const overallRows =
        scheduleEnabled
          ? [assignment]
          : await patchRow(
              'course_assignments',
              assignment.id,
              {
                open_date:
                  overall.open_date,
                due_date:
                  overall.due_date,
                close_date:
                  overall.due_date
              },
              'Saving overall form dates'
            );

      for (const id of deletedIds) {
        await deleteInstance(id);
      }

      for (
        const id of deletedScheduleItemIds
      ) {
        await deleteStructuredScheduleItem(
          id
        );
      }
      for (const instance of instances) {
        let saved = instance;

        if (!instance.locked) {
          saved = await saveInstance(
            instance,
            assignment.id
          );

          await replaceInstanceSections(
            saved.id,
            instance.section_ids
          );
        }

        if (scheduleEnabled) {
          await saveScheduleForInstance(
            instance.id,
            saved.id
          );
        }

        if (
          !scheduleEnabled &&
          groupDeadlinesEnabled &&
          assignment.groups_enabled
        ) {
          const deadlinesForInstance =
            groupDeadlines[instance.id] || {};

          for (
            let groupNumber = 1;
            groupNumber <=
              Number(assignment.group_count || 0);
            groupNumber += 1
          ) {
            await saveGroupDeadline(
              saved.id,
              groupNumber,
              deadlinesForInstance[
                groupNumber
              ] || {}
            );
          }
        }
      }

      const refreshed =
        await loadConfiguration(
          assignment.id
        );

      const refreshedScheduleItems =
        await loadStructuredScheduleItems(
          refreshed.instances.map(
            instance => instance.id
          )
        );

      setSections(refreshed.sections);
      setInstances(refreshed.instances);
      setStructuredScheduleItems(
        refreshedScheduleItems
      );
      setDeletedScheduleItemIds([]);
      setGroupDeadlines(
        buildGroupDeadlineDraft(
          refreshed.groupDeadlines
        )
      );
      setDeletedIds([]);
      setSuccess('Structured Form instances saved.');
      onAssignmentChanged?.(overallRows?.[0] || {
        ...assignment,
        open_date: overall.open_date,
        due_date: overall.due_date,
        close_date: overall.due_date
      });
    } catch (err) {
      setError(err?.message || 'Could not save the Structured Form instances.');
    } finally {
      setSaving(false);
    }
  }

  const structuredCalendarMarkers =
    scheduleEnabled
      ? [
          localChoiceEnabled &&
          choiceOpenDate
            ? {
                date:
                  choiceOpenDate,
                kind:
                  'open',
                detail:
                  label +
                  ' Schedule choices open'
              }
            : null,

          localChoiceEnabled &&
          choiceCloseDate
            ? {
                date:
                  choiceCloseDate,
                kind:
                  'due',
                detail:
                  label +
                  ' Schedule choices close'
              }
            : null,

          ...instances.flatMap(
            instance => {
              const mode =
                localChoiceEnabled
                  ? 'choice'
                  : 'single';

              return itemsForInstance(
                instance.id,
                mode
              ).flatMap(
                item => {
                  const source =
                    structuredCalendarScheduleSource(
                      item,
                      scheduleReadings,
                      scheduleEvents
                    );

                  if (
                    !source.date
                  ) {
                    return [];
                  }

                  const openDate =
                    structuredCalendarAddDays(
                      source.date,
                      instance
                        .schedule_open_offset_days
                    );

                  const closeDate =
                    structuredCalendarAddDays(
                      source.date,
                      instance
                        .schedule_close_offset_days
                    );

                  const possible =
                    localChoiceEnabled
                      ? ' possible'
                      : '';

                  return [
                    openDate
                      ? {
                          date:
                            openDate,
                          kind:
                            'open',
                          detail:
                            'Instance ' +
                            instance
                              .instance_order +
                            possible +
                            ' opens · ' +
                            source.label
                        }
                      : null,

                    closeDate
                      ? {
                          date:
                            closeDate,
                          kind:
                            'due',
                          detail:
                            'Instance ' +
                            instance
                              .instance_order +
                            possible +
                            ' closes · ' +
                            source.label
                        }
                      : null
                  ].filter(Boolean);
                }
              );
            }
          )
        ].filter(Boolean)

      : [
          overall.open_date
            ? {
                date:
                  overall.open_date,
                kind:
                  'open',
                detail:
                  label +
                  ' overall opens'
              }
            : null,

          overall.due_date
            ? {
                date:
                  overall.due_date,
                kind:
                  'due',
                detail:
                  label +
                  ' final due'
              }
            : null,

          ...instances.flatMap(
            instance => [
              instance.open_date
                ? {
                    date:
                      instance.open_date,
                    kind:
                      'open',
                    detail:
                      'Instance ' +
                      instance
                        .instance_order +
                      ' opens'
                  }
                : null,

              instance.close_date
                ? {
                    date:
                      instance.close_date,
                    kind:
                      'due',
                    detail:
                      'Instance ' +
                      instance
                        .instance_order +
                      ' closes'
                  }
                : null
            ]
          )
        ].filter(Boolean);


  function getPreviousStructuredInstance(
    instance
  ) {
    if (!instance) {
      return null;
    }

    const currentOrder =
      Number(
        instance.instance_order
      ) || 0;

    if (currentOrder <= 1) {
      return null;
    }

    return (
      instances
        .filter(
          candidate =>
            (
              Number(
                candidate.instance_order
              ) || 0
            ) <
              currentOrder
        )
        .sort(
          (a, b) =>
            (
              Number(
                b.instance_order
              ) || 0
            ) -
            (
              Number(
                a.instance_order
              ) || 0
            )
        )[0] ||
      null
    );
  }


  function getPreviousStructuredCloseDate(
    instance
  ) {
    const previous =
      getPreviousStructuredInstance(
        instance
      );

    return (
      previous?.close_date ||
      previous?.open_date ||
      ''
    );
  }


  function getPreviousStructuredGroupCloseDate(
    instance,
    groupNumber
  ) {
    const previous =
      getPreviousStructuredInstance(
        instance
      );

    if (!previous) {
      return '';
    }

    const previousDeadline =
      groupDeadlines[
        previous.id
      ]?.[
        groupNumber
      ] || null;

    return (
      previousDeadline?.close_date ||
      previous?.close_date ||
      previousDeadline?.open_date ||
      previous?.open_date ||
      ''
    );
  }


  const datePickerContext = {
    course,
    weeks:
      calendarWeeks,
    assignments:
      calendarAssignments,
    typeSettings:
      calendarTypeSettings,
    scheduleItems:
      calendarScheduleItems,
    scheduleReadings,
    scheduleEvents,
    holidays:
      calendarHolidays,
    extraMarkers:
      structuredCalendarMarkers
  };

  if (loading) {
    return (
      <div style={styles.muted}>
        Loading form instances...
      </div>
    );
  }

  return (
    <div style={styles.wrap}>
      <div style={styles.intro}>
        <strong>{label}</strong> is one evolving form. During an instance, students may edit exactly the sections selected for that instance. Submitting creates one immutable, gradable snapshot and locks the form until a later instance opens.
      </div>
      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      {!scheduleEnabled && (
      <div style={styles.overall}>
        <div style={styles.blockTitle}>Overall form dates</div>
        <div style={styles.help}>The Final due date is the permanent closing date for the whole form.</div>
        <div style={styles.dateGrid}>
          <label style={styles.dateField}>
            <span style={styles.dateLabel}>Open</span>
                        <CourseDatePicker
              {...datePickerContext}
              value={
                overall.open_date
              }
              onChange={
                value =>
                  setOverall(
                    previous => ({
                      ...previous,
                      open_date:
                        value
                    })
                  )
              }
            />
          </label>
          <label style={styles.dateField}>
            <span style={styles.dateLabel}>Final due</span>
                        <CourseDatePicker
              {...datePickerContext}
              value={
                overall.due_date
              }
              initialMonthDate={
                overall.open_date ||
                ''
              }
              onChange={
                value =>
                  setOverall(
                    previous => ({
                      ...previous,
                      due_date:
                        value
                    })
                  )
              }
            />
          </label>
        </div>
      </div>
      )}

      {scheduleEnabled && (
        <div style={styles.scheduleIntro}>
          <div style={styles.blockTitle}>
            Scheduled form instances
          </div>

          <div style={styles.help}>
            Each Instance has its own reading/event anchor and its own
            relative Open and Close offsets. Negative offsets are before
            the anchor; positive offsets are after it.
          </div>

          <label style={styles.choiceToggle}>
            <input
              type="checkbox"
              checked={localChoiceEnabled}
              disabled={scheduleSettingSaving}
              onChange={event =>
                handleChoiceModeChange(
                  event.target.checked
                )
              }
            />
            <span>
              Allow students/groups to choose the anchor for each Instance
            </span>
          </label>

          {localChoiceEnabled && (
            <div style={styles.choiceWindow}>
              <label style={styles.dateField}>
                <span style={styles.dateLabel}>
                  Choice opens
                </span>
                                <CourseDatePicker
                  {...datePickerContext}
                  value={
                    choiceOpenDate
                  }
                  onChange={
                    setChoiceOpenDate
                  }
                />
              </label>

              <label style={styles.dateField}>
                <span style={styles.dateLabel}>
                  Choice closes
                </span>
                                <CourseDatePicker
                  {...datePickerContext}
                  value={
                    choiceCloseDate
                  }
                  initialMonthDate={
                    choiceOpenDate ||
                    ''
                  }
                  onChange={
                    setChoiceCloseDate
                  }
                />
              </label>

              <button
                type="button"
                style={styles.secondaryBtn}
                disabled={scheduleSettingSaving}
                onClick={saveChoiceWindow}
              >
                {scheduleSettingSaving
                  ? 'Saving...'
                  : 'Save Choice Window'}
              </button>
            </div>
          )}
        </div>
      )}

      {!sections.length && <div style={styles.empty}>This assignment has no form snapshot. Remove it from the course and add it again after saving the form in the Assignment Library.</div>}

      {instances.map(instance => (
        <div key={instance.id} style={styles.instance}>
          <div style={styles.instanceHeading}>
            <div>
              <div style={styles.instanceTitle}>Instance {instance.instance_order}</div>
              <div style={styles.help}>One submission, one set of comments, and one grade/status.</div>
            </div>
            {instance.locked ? (
              <span style={styles.lockedPill}>Has submissions · locked</span>
            ) : (
              <button type="button" style={styles.deleteBtn} onClick={() => removeInstance(instance)}>Delete Instance</button>
            )}
          </div>

          {!scheduleEnabled ? (
            <div style={styles.dateGrid}>
              <label style={styles.dateField}>
                <span style={styles.dateLabel}>Open</span>
                                <CourseDatePicker
                  {...datePickerContext}
                  value={
                    instance.open_date
                  }
                  initialMonthDate={
                    getPreviousStructuredCloseDate(
                      instance
                    )
                  }
                  disabled={
                    instance.locked
                  }
                  onChange={
                    value =>
                      updateInstance(
                        instance.id,
                        {
                          open_date:
                            value
                        }
                      )
                  }
                />
              </label>
              <label style={styles.dateField}>
                <span style={styles.dateLabel}>Close</span>
                                <CourseDatePicker
                  {...datePickerContext}
                  value={
                    instance.close_date
                  }
                  initialMonthDate={
                    instance.open_date ||
                    ''
                  }
                  disabled={
                    instance.locked
                  }
                  onChange={
                    value =>
                      updateInstance(
                        instance.id,
                        {
                          close_date:
                            value
                        }
                      )
                  }
                />
              </label>
            </div>
          ) : (
            <div style={styles.instanceScheduleBox}>
              <div style={styles.dateGrid}>
                <label style={styles.dateField}>
                  <span style={styles.dateLabel}>
                    Open offset (days)
                  </span>
                  <input
                    type="number"
                    step="1"
                    style={styles.dateInput}
                    value={
                      instance.schedule_open_offset_days
                    }
                    disabled={instance.locked}
                    onChange={event =>
                      updateInstance(
                        instance.id,
                        {
                          schedule_open_offset_days:
                            event.target.value
                        }
                      )
                    }
                  />
                </label>

                <label style={styles.dateField}>
                  <span style={styles.dateLabel}>
                    Close offset (days)
                  </span>
                  <input
                    type="number"
                    step="1"
                    style={styles.dateInput}
                    value={
                      instance.schedule_close_offset_days
                    }
                    disabled={instance.locked}
                    onChange={event =>
                      updateInstance(
                        instance.id,
                        {
                          schedule_close_offset_days:
                            event.target.value
                        }
                      )
                    }
                  />
                </label>
              </div>

              {!localChoiceEnabled ? (() => {
                const single =
                  itemsForInstance(
                    instance.id,
                    'single'
                  )[0] || null;

                return (
                  <label style={styles.scheduleAnchorField}>
                    <span style={styles.dateLabel}>
                      Anchor
                    </span>

                    <select
                      style={styles.dateInput}
                      value={
                        scheduleItemValue(
                          single
                        )
                      }
                      disabled={instance.locked}
                      onChange={event =>
                        setSingleAnchor(
                          instance.id,
                          event.target.value
                        )
                      }
                    >
                      <option value="">
                        Select a reading or event...
                      </option>

                      {availableScheduleSources.map(
                        option => (
                          <option
                            key={option.value}
                            value={option.value}
                          >
                            {option.label}
                          </option>
                        )
                      )}
                    </select>
                  </label>
                );
              })() : (() => {
                const choices =
                  itemsForInstance(
                    instance.id,
                    'choice'
                  );

                const chosenValues =
                  new Set(
                    choices.map(
                      scheduleItemValue
                    )
                  );

                const available =
                  availableScheduleSources.filter(
                    option =>
                      !chosenValues.has(
                        option.value
                      )
                  );

                return (
                  <div style={styles.choicePool}>
                    <div style={styles.schedulePoolHeader}>
                      Choice options
                    </div>

                    {choices.length === 0 ? (
                      <div style={styles.help}>
                        No options added yet.
                      </div>
                    ) : (
                      choices.map(item => {
                        const option =
                          availableScheduleSources.find(
                            candidate =>
                              candidate.value ===
                              scheduleItemValue(item)
                          );

                        return (
                          <div
                            key={item.id}
                            style={styles.choiceOptionRow}
                          >
                            <div style={styles.choiceOptionLabel}>
                              {option?.label ||
                                scheduleItemValue(item)}
                            </div>

                            <label style={styles.capacityField}>
                              <span style={styles.dateLabel}>
                                Capacity
                              </span>
                              <input
                                type="number"
                                min="1"
                                step="1"
                                style={styles.capacityInput}
                                value={
                                  item.capacity ?? ''
                                }
                                disabled={
                                  instance.locked ||
                                  item.hasSelections
                                }
                                placeholder="Unlimited"
                                onChange={event =>
                                  updateScheduleItemLocal(
                                    item.id,
                                    {
                                      capacity:
                                        event.target.value
                                    }
                                  )
                                }
                              />
                            </label>

                            <button
                              type="button"
                              style={styles.deleteBtn}
                              disabled={
                                instance.locked ||
                                item.hasSelections
                              }
                              title={
                                item.hasSelections
                                  ? 'Students/groups have already chosen this option.'
                                  : ''
                              }
                              onClick={() =>
                                removeScheduleItemLocal(
                                  item
                                )
                              }
                            >
                              Remove
                            </button>
                          </div>
                        );
                      })
                    )}

                    <select
                      style={styles.dateInput}
                      value=""
                      disabled={
                        instance.locked ||
                        available.length === 0
                      }
                      onChange={event => {
                        if (
                          event.target.value
                        ) {
                          addChoiceOption(
                            instance.id,
                            event.target.value
                          );
                        }
                      }}
                    >
                      <option value="">
                        + Add reading/event option...
                      </option>

                      {available.map(option => (
                        <option
                          key={option.value}
                          value={option.value}
                        >
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })()}
            </div>
          )}

          {!scheduleEnabled &&
            groupDeadlinesEnabled &&
            assignment.groups_enabled &&
            Number(assignment.group_count) > 0 && (
            <div style={styles.groupDeadlineBox}>
              <div style={styles.groupDeadlineTitle}>
                Group-specific dates
              </div>

              <div style={styles.help}>
                Leave a field blank to use the default Instance date above.
                Assignment extensions are added after the group's Close date.
              </div>

              <div style={styles.groupDeadlineRows}>
                {Array.from(
                  {
                    length:
                      Number(
                        assignment.group_count
                      ) || 0
                  },
                  (_, index) => index + 1
                ).map(groupNumber => {
                  const deadline =
                    groupDeadlines[
                      instance.id
                    ]?.[groupNumber] || {};

                  return (
                    <div
                      key={groupNumber}
                      style={
                        styles.groupDeadlineRow
                      }
                    >
                      <div
                        style={
                          styles.groupDeadlineName
                        }
                      >
                        {groupNameFor(
                          groupNumber
                        )}
                      </div>

                      <label
                        style={
                          styles.dateField
                        }
                      >
                        <span
                          style={
                            styles.dateLabel
                          }
                        >
                          Open
                        </span>

                                                <CourseDatePicker
                          {...datePickerContext}
                          value={
                            deadline.open_date ||
                            ''
                          }
                          initialMonthDate={
                            getPreviousStructuredGroupCloseDate(
                              instance,
                              groupNumber
                            ) ||
                            instance.open_date ||
                            ''
                          }
                          emptyLabel={
                            instance.open_date
                              ? `Use Instance date (${instance.open_date})`
                              : 'Select date'
                          }
                          onChange={
                            value =>
                              updateGroupDeadline(
                                instance.id,
                                groupNumber,
                                {
                                  open_date:
                                    value
                                }
                              )
                          }
                        />
                      </label>

                      <label
                        style={
                          styles.dateField
                        }
                      >
                        <span
                          style={
                            styles.dateLabel
                          }
                        >
                          Close
                        </span>

                                                <CourseDatePicker
                          {...datePickerContext}
                          value={
                            deadline.close_date ||
                            ''
                          }
                          initialMonthDate={
                            deadline.open_date ||
                            instance.open_date ||
                            ''
                          }
                          emptyLabel={
                            instance.close_date
                              ? `Use Instance date (${instance.close_date})`
                              : 'Select date'
                          }
                          onChange={
                            value =>
                              updateGroupDeadline(
                                instance.id,
                                groupNumber,
                                {
                                  close_date:
                                    value
                                }
                              )
                          }
                        />
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div style={styles.sectionLabel}>Sections open for editing</div>
          <div style={styles.sectionChecks}>
            {sections.map(section => (
              <label key={section.id} style={styles.sectionCheck}>
                <input type="checkbox" checked={instance.section_ids.includes(section.id)} disabled={instance.locked} onChange={() => toggleSection(instance, section.id)} />
                <span>{section.title}</span>
              </label>
            ))}
          </div>
        </div>
      ))}

      <div style={styles.actions}>
        <button type="button" style={styles.secondaryBtn} disabled={!sections.length || saving} onClick={() => setInstances(previous => [
          ...previous,
          newInstance(Math.max(0, ...previous.map(row => Number(row.instance_order) || 0)) + 1)
        ])}>+ Add Instance</button>
        <button type="button" style={styles.saveBtn} disabled={!sections.length || saving} onClick={handleSave}>{saving ? 'Saving...' : 'Save Instances'}</button>
      </div>
    </div>
  );
}

const styles = {
  wrap: { display: 'grid', gap: '14px' },
  intro: { padding: '12px 14px', background: '#eef6fb', border: '1px solid #c8ddea', borderRadius: '8px', color: '#45677d', fontSize: '12px', lineHeight: 1.55 },
  overall: { padding: '14px', border: '1px solid #cbd5df', borderRadius: '8px', background: '#f8fafb' },
  scheduleIntro: {
    padding: '14px',
    border: '1px solid #cbd5df',
    borderRadius: '8px',
    background: '#f8fafb'
  },
  choiceToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '12px',
    color: '#334e68',
    fontSize: '12px',
    fontWeight: 700
  },
  choiceWindow: {
    display: 'grid',
    gridTemplateColumns:
      'minmax(150px, 1fr) minmax(150px, 1fr) auto',
    gap: '10px',
    alignItems: 'end',
    marginTop: '12px'
  },
  instanceScheduleBox: {
    marginTop: '12px',
    padding: '12px',
    border: '1px solid #d9e2ec',
    borderRadius: '8px',
    background: '#f8fafb'
  },
  scheduleAnchorField: {
    display: 'grid',
    gap: '5px',
    marginTop: '10px'
  },
  choicePool: {
    display: 'grid',
    gap: '8px',
    marginTop: '10px'
  },
  schedulePoolHeader: {
    color: '#486581',
    fontSize: '11px',
    fontWeight: 800
  },
  choiceOptionRow: {
    display: 'grid',
    gridTemplateColumns:
      'minmax(200px, 1fr) 110px auto',
    gap: '9px',
    alignItems: 'end',
    padding: '8px 0',
    borderBottom: '1px solid #e6ebef'
  },
  choiceOptionLabel: {
    color: '#334e68',
    fontSize: '12px',
    lineHeight: 1.45,
    paddingBottom: '8px'
  },
  capacityField: {
    display: 'grid',
    gap: '4px'
  },
  capacityInput: {
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid #bcccdc',
    borderRadius: '6px',
    padding: '8px 9px',
    background: 'white',
    color: '#243b53'
  },
  instance: { padding: '15px', border: '1px solid #d9e2ec', borderRadius: '9px', background: 'white' },
  instanceHeading: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '13px' },
  instanceTitle: { color: '#243b53', fontSize: '15px', fontWeight: 800 },
  blockTitle: { color: '#243b53', fontSize: '14px', fontWeight: 800 },
  help: { marginTop: '4px', color: '#627d98', fontSize: '11px', lineHeight: 1.45 },
  dateGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px', marginTop: '12px' },
  dateField: { display: 'grid', gap: '5px' },
  dateLabel: { color: '#486581', fontSize: '10px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em' },
  dateInput: { width: '100%', boxSizing: 'border-box', border: '1px solid #bcccdc', borderRadius: '6px', padding: '8px 9px', background: 'white', color: '#243b53' },
  groupDeadlineBox: {
    marginTop: '14px',
    padding: '12px',
    border: '1px solid #d9e2ec',
    borderRadius: '8px',
    background: '#f8fafb'
  },
  groupDeadlineTitle: {
    color: '#243b53',
    fontSize: '13px',
    fontWeight: 800
  },
  groupDeadlineRows: {
    display: 'grid',
    gap: '9px',
    marginTop: '10px'
  },
  groupDeadlineRow: {
    display: 'grid',
    gridTemplateColumns:
      'minmax(110px, 0.7fr) minmax(150px, 1fr) minmax(150px, 1fr)',
    gap: '9px',
    alignItems: 'end'
  },
  groupDeadlineName: {
    color: '#486581',
    fontSize: '12px',
    fontWeight: 800,
    paddingBottom: '9px'
  },
  sectionLabel: { marginTop: '14px', color: '#486581', fontSize: '11px', fontWeight: 800 },
  sectionChecks: { display: 'grid', gap: '7px', marginTop: '8px' },
  sectionCheck: { display: 'flex', alignItems: 'center', gap: '8px', color: '#334e68', fontSize: '12px' },
  actions: { display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' },
  saveBtn: { border: 'none', borderRadius: '7px', background: '#2f6f9f', color: 'white', padding: '9px 14px', fontWeight: 800, cursor: 'pointer' },
  secondaryBtn: { border: '1px solid #9fb3c8', borderRadius: '7px', background: 'white', color: '#334e68', padding: '8px 12px', fontWeight: 700, cursor: 'pointer' },
  deleteBtn: { border: '1px solid #e3aaaa', borderRadius: '6px', background: '#fff7f7', color: '#a12a2a', padding: '6px 9px', fontSize: '11px', cursor: 'pointer' },
  lockedPill: { borderRadius: '999px', background: '#edf2f7', color: '#526d82', padding: '5px 9px', fontSize: '10px', fontWeight: 700 },
  muted: { color: '#627d98', fontSize: '12px' },
  empty: { padding: '14px', border: '1px dashed #cbd5df', borderRadius: '8px', color: '#627d98', fontSize: '12px' },
  error: { padding: '10px 12px', background: '#faeaea', border: '1px solid #e8bcbc', borderRadius: '7px', color: '#a12a2a', fontSize: '12px' },
  success: { padding: '10px 12px', background: '#e7f5ed', border: '1px solid #b9dfc9', borderRadius: '7px', color: '#216e45', fontSize: '12px' }
};
